import { cn } from "@z/ui";
import type { ComponentProps } from "react";
import Markdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";

export interface NotePreviewProps {
  markdown: string;
  className?: string;
}

/*
 * Prose styling with arbitrary variants on the wrapper so consumers need no extra stylesheet:
 * Tailwind picks these up through the app's `@source "../../packages/*\/src"`.
 */
const proseClassName = [
  "text-base text-fg leading-relaxed break-words",
  "[&>*+*]:mt-3",
  "[&_h1]:text-2xl [&_h1]:font-semibold [&_h1]:mt-6 [&_h1]:mb-2",
  "[&_h2]:text-xl [&_h2]:font-semibold [&_h2]:mt-5 [&_h2]:mb-2",
  "[&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-4",
  "[&_h4]:font-semibold [&_h5]:font-semibold [&_h6]:font-semibold",
  "[&_a]:text-accent [&_a]:underline [&_a]:underline-offset-2",
  "[&_strong]:font-semibold [&_em]:italic",
  "[&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-6 [&_ol]:pl-6 [&_li+li]:mt-1",
  "[&_li>ul]:mt-1 [&_li>ol]:mt-1",
  "[&_.task-list-item]:list-none [&_.task-list-item]:-ml-6 [&_.task-list-item]:flex [&_.task-list-item]:items-baseline [&_.task-list-item]:gap-2",
  "[&_input[type=checkbox]]:accent-accent",
  "[&_blockquote]:border-l-2 [&_blockquote]:border-border-strong [&_blockquote]:pl-4 [&_blockquote]:text-fg-muted",
  "[&_code]:font-mono [&_code]:text-sm [&_code]:rounded-sm [&_code]:bg-surface [&_code]:px-1 [&_code]:py-0.5",
  "[&_pre]:font-mono [&_pre]:text-sm [&_pre]:rounded-md [&_pre]:border [&_pre]:border-border [&_pre]:bg-surface [&_pre]:p-3 [&_pre]:overflow-x-auto",
  "[&_pre_code]:bg-transparent [&_pre_code]:p-0",
  "[&_hr]:border-border [&_hr]:my-6",
  "[&_table]:w-full [&_table]:border-collapse [&_table]:text-sm",
  "[&_th]:border [&_th]:border-border [&_th]:bg-surface [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_th]:font-medium",
  "[&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1",
  "[&_img]:max-w-full [&_img]:rounded-md",
  "[&_del]:text-fg-muted",
].join(" ");

function Link({ node: _node, ...props }: ComponentProps<"a"> & { node?: unknown }) {
  return <a {...props} target="_blank" rel="noreferrer" />;
}

function Checkbox({ node: _node, ...props }: ComponentProps<"input"> & { node?: unknown }) {
  return <input {...props} disabled readOnly />;
}

const components: Components = { a: Link, input: Checkbox };

/** Read-only GFM rendering. Raw HTML in the note is dropped, never rendered. */
export function NotePreview({ markdown, className }: NotePreviewProps) {
  return (
    <div data-note-preview="" className={cn(proseClassName, className)}>
      <Markdown remarkPlugins={[remarkGfm]} skipHtml components={components}>
        {markdown}
      </Markdown>
    </div>
  );
}
